import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AppErrorStateMatcher, AuthService, SSOConfig } from 'src/app/shared/services';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonService } from 'src/app/shared/services/common.service';
import validator from 'validator';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
  public loading = 0;

  public email: string;
  public phone: string;

  /** True once the username field holds something phone-shaped: digits and phone punctuation only, at least 3 digits. */
  get showSmsConsent(): boolean {
    const value = (this.checkUserForm?.controls.username.value || '').trim();
    return /^\+?[\d\s().-]+$/.test(value) && value.replace(/\D/g, '').length >= 3;
  }

  public magicLinkMsg: string;
  public magicError: string;
  public magicCode: string;
  public magicTTL: number;
  /** Email account that has never set a password: skip the password page and send a magic link */
  public passwordless = false;

  public checkUserMessage: string;

  public matcher = new AppErrorStateMatcher();

  public checkUserForm: UntypedFormGroup = this.fb.group({
    username: ['', [Validators.required]],
  });
  public loginForm: UntypedFormGroup = this.fb.group({
    password: ['', [Validators.required]],
  });
  public phoneForm: UntypedFormGroup = this.fb.group({
    otp: ['', [Validators.required]],
  });

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private commonService: CommonService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.has('m')) {
      this.magicCode = this.route.snapshot.queryParamMap.get('m');
      this.magicTTL = Number(this.route.snapshot.queryParamMap.get('ttl')); // use it to show magic count down
      this.loginWithMagicCode(this.magicCode);
    }
    this.route.fragment.subscribe(fragment => {
      const params = new URLSearchParams(fragment || '');
      const code = params.get('code');
      if (code) {
        this.loading++;
        const ssoType = this.authService.getSSOType();
        if (ssoType === 'okta') {
          this.loginWithOktaSSO(code);
        } else {
          this.loginWithAzureSSO(code);
        }
      }
    });
  }

  loginWithAzureSSO(code: string) {
    this.loading++;
    this.authService
      .loginWithAzureSSO(code)
      .toPromise()
      .then((resp: any) => {
        this.commonService.notification('Login successful', 'success');
        this.router.navigateByUrl(resp.redirectUrl);
      })
      .catch((resp: HttpErrorResponse) => {
        if (resp.error.error) {
          this.commonService.errorNotification(resp.error.error);
        }
      })
      .finally(() => {
        this.loading--;
      });
  }

  loginWithOktaSSO(code: string) {
    this.loading++;
    this.authService
      .loginWithOktaSSO(code)
      .toPromise()
      .then((resp: any) => {
        this.commonService.notification('Login successful', 'success');
        this.router.navigateByUrl(resp.redirectUrl);
      })
      .catch((resp: HttpErrorResponse) => {
        if (resp.error.error) {
          this.commonService.errorNotification(resp.error.error);
        }
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Redirect to SSO provider login page
   */
  private redirectToSSO(ssoConfig: SSOConfig, email: string) {
    const ssoUrl = this.authService.initializeSSOFlow(ssoConfig, email);
    window.location.href = ssoUrl;
  }

  /**
   * Check if the input is an email address
   * Supports both standard emails and Microsoft UPN format (e.g., user#ext#@tenant.onmicrosoft.com)
   */
  private isEmailAddress(input: string): boolean {
    if (validator.isEmail(input)) {
      return true;
    }
    const microsoftUpnRegex = /^[^@]+#ext#@[^@]+\.onmicrosoft\.com$/i;
    return microsoftUpnRegex.test(input);
  }

  /**
   * checks if user exists in system
   */
  checkUser() {
    if (this.checkUserForm.status === 'INVALID') {
      return;
    }

    let { username } = this.checkUserForm.value;
    username = username.trim();
    const isEmail = this.isEmailAddress(username);
    const email = isEmail ? username : null;
    const phone = !isEmail ? username : null;

    this.loading++;
    this.authService
      .checkUser(email, phone)
      .then((resp: any) => {
        if (resp.sso_flag && resp.sso_type && resp.sso_url && resp.sso_client_id) {
          const ssoConfig: SSOConfig = {
            sso_flag: resp.sso_flag,
            sso_type: resp.sso_type,
            sso_url: resp.sso_url,
            sso_client_id: resp.sso_client_id,
          };
          this.redirectToSSO(ssoConfig, email);
          return;
        }

        this.email = isEmail ? username : null;
        this.phone = !isEmail ? username : null;

        if (this.phone) {
          this.requestOTP(this.phone);
        }

        // Non-SSO email account that never set a password: there is nothing to
        // type on the password page, so email a sign-in link straight away.
        if (this.email && resp.has_password === false) {
          this.passwordless = true;
          this.getMagicLink();
        }
      })
      .catch((resp: HttpErrorResponse | any) => {
        let errorResponse: HttpErrorResponse;
        if (typeof resp === 'function') {
          try {
            const result = resp();
            errorResponse = result instanceof HttpErrorResponse ? result : result;
          } catch (e) {
            errorResponse = resp as any;
          }
        } else {
          errorResponse = resp;
        }

        if (errorResponse && typeof errorResponse === 'object' && errorResponse.status === 422) {
          this.matcher.setServerErrors(this.checkUserForm, errorResponse, { email: 'username', phone: 'username' });
          return;
        }

        let error: HttpErrorResponse;

        if (typeof resp === 'function') {
          try {
            const result = resp();
            error = result instanceof HttpErrorResponse ? result : result?.error || result;
          } catch (e) {
            error = resp as any;
          }
        } else {
          error = resp;
        }

        const errorMessage =
          error?.error?.error ||
          error?.error?.message ||
          (typeof error?.error === 'string' ? error.error : null) ||
          error?.message ||
          (error?.status === 404
            ? 'User not found. Please check your email or phone number.'
            : 'An error occurred. Please try again.');

        if (errorMessage) {
          const usernameControl = this.checkUserForm.controls.username;
          usernameControl.setErrors({ error: errorMessage });
          usernameControl.markAsDirty();
          usernameControl.markAsTouched();
          this.checkUserMessage = errorMessage;
        }
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Login with password input
   */
  loginWithPassword() {
    if (this.loginForm.status === 'INVALID') {
      return;
    }

    const { password } = this.loginForm.value;
    this.loading++;
    this.authService
      .login(this.email, password)
      .toPromise()
      .then((resp: any) => {
        this.commonService.notification('Login successful', 'success');
        this.router.navigateByUrl(resp.redirectUrl);
      })
      .catch((resp: HttpErrorResponse | any) => {
        let errorResponse: HttpErrorResponse;
        if (typeof resp === 'function') {
          try {
            const result = resp();
            errorResponse = result instanceof HttpErrorResponse ? result : result;
          } catch (e) {
            errorResponse = resp as any;
          }
        } else {
          errorResponse = resp;
        }

        if (errorResponse && typeof errorResponse === 'object' && errorResponse.status === 422) {
          this.matcher.setServerErrors(this.loginForm, errorResponse);
          return;
        }

        if (errorResponse && typeof errorResponse === 'object' && errorResponse.status === 401) {
          const passwordControl = this.loginForm.controls.password;
          passwordControl.setErrors({ error: 'Please check your login credentials.' });
          passwordControl.markAsDirty();
          passwordControl.markAsTouched();
          this.cdr.detectChanges();
          return;
        }

        let error: HttpErrorResponse;
        if (typeof resp === 'function') {
          try {
            const result = resp();
            error = result instanceof HttpErrorResponse ? result : result?.error || result;
          } catch (e) {
            error = resp as any;
          }
        } else {
          error = resp;
        }

        const errorMessage =
          error?.error?.error ||
          error?.error?.message ||
          (typeof error?.error === 'string' ? error.error : null) ||
          error?.message ||
          'An error occurred. Please try again.';
        if (errorMessage) {
          this.commonService.notification(errorMessage, 'error');
        }
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Request OTP
   * @param phone phone number
   */
  requestOTP(phone) {
    this.loading++;
    this.authService
      .requestOTP(phone)
      .then((resp: any) => {
        if (resp.message) {
          this.commonService.notification(resp.message, 'success');
        }
      })
      .catch((resp: HttpErrorResponse) => {
        if (resp.error.error) {
          this.commonService.notification(resp.error.error, 'error');
        }
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Login with phone otp
   */
  loginWithPhone() {
    if (this.phoneForm.status === 'INVALID') {
      return;
    }

    const { otp } = this.phoneForm.value;
    this.loading++;
    this.authService
      .loginWithOTP(this.phone, otp)
      .then((resp: any) => {
        this.commonService.notification('Login successful', 'success');
        this.router.navigateByUrl(resp.redirectUrl);
      })
      .catch((resp: HttpErrorResponse) => {
        if (resp.status === 422) {
          this.matcher.setServerErrors(this.phoneForm, resp);
          return;
        }

        if (resp.status === 401) {
          this.phoneForm.controls.otp.setErrors({ error: 'Invalid OTP' });
          return;
        }

        if (resp.error.error) {
          this.commonService.notification(resp.error.error, 'error');
        }
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Request magic link to given email
   */
  getMagicLink() {
    this.loading++;
    this.authService
      .getMagicLink(this.email)
      .then((resp: any) => {
        if (resp.message) {
          this.magicLinkMsg = resp.message;
        }
      })
      .catch((resp: HttpErrorResponse) => {
        if (resp.error.error) {
          this.commonService.notification(resp.error.error, 'error');
        }
        if (this.passwordless) {
          // Nothing else to show for a passwordless account; go back to the email step
          this.resetLogin();
        }
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Attempt to login with magic link
   * @param code magic link code
   */
  loginWithMagicCode(code) {
    this.loading++;
    this.authService
      .loginWithMagicCode(code)
      .then((resp: any) => {
        this.commonService.notification('Login successful', 'success');
        this.router.navigateByUrl(resp.redirectUrl);
      })
      .catch((resp: HttpErrorResponse) => {
        if (resp.error.error) {
          this.magicError = resp.error.error;
          return;
        }

        this.commonService.notification('Unable to login with magic link', 'danger');
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Reset login params
   */
  resetLogin() {
    this.email = null;
    this.phone = null;
    this.magicCode = null;
    this.magicError = null;
    this.magicLinkMsg = null;
    this.passwordless = false;
    this.router.navigate(['/login']);
  }
}
